import { memo, useState } from 'react';
import PropTypes from 'prop-types';

import { navigateTo } from '../../../../utils/navigateTo';
import { propTypes } from '../../ThemedPrice';

const inlineStylesHidden = { display: 'none' };
const inlineStylesShown = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  height: '100%',
  width: '100%',
  zIndex: 3
};

const noop = () => {};
const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

const ThemedHomecardWrapper = ({
  link,
  className = '',
  newTab = true,
  onClick = noop,
  onMouseEnter = noop,
  onMouseLeave = noop,
  onTouchEnd = noop,
  onTouchStart = noop,
  renderContent = noop,
  renderImage = noop,
  selected = false,
  theme = EMPTY_OBJECT,
  photos = EMPTY_ARRAY,
  renderExtraContent = noop,
  renderDescription = noop,
  dataTest = '',
  onMoreInfoClick = noop,
  description = null,
  isBooked = false,
  ...restProps
}) => {
  const [isDescriptionVisible, setIsDescriptionVisible] = useState(false);
  const homecardTarget = newTab ? '_blank' : '_self';
  const hasDescription = description !== null;

  const handleClick = async e => {
    e.preventDefault();
    await onClick();
    if (!isBooked) {
      navigateTo(link, { newTab });
    }
  };

  const onMoreInfoClickHandler = e => {
    e.stopPropagation();
    e.preventDefault();
    onMoreInfoClick();
    setIsDescriptionVisible(true);
  };

  const onCloseClick = e => {
    e.stopPropagation();
    e.preventDefault();
    setIsDescriptionVisible(false);
  };

  const Component = isBooked ? 'span' : 'a';
  const componentProps = isBooked
    ? {}
    : {
        href: link,
        target: homecardTarget,
        onClick,
        onMouseEnter,
        onMouseLeave,
        onTouchEnd,
        onTouchStart
      };

  const otherProps = {
    ...restProps,
    className,
    selected,
    photos
  };

  return (
    <>
      <Component
        data-test={dataTest}
        className={theme.wrapper}
        {...componentProps}
      >
        {renderImage({
          ...otherProps,
          isBooked,
          onClick: handleClick,
          className: restProps.imageClassName
        })}
      </Component>
      <>
        {renderContent({
          ...otherProps,
          isBooked,
          hasDescription,
          onMoreInfoClick: onMoreInfoClickHandler,
          link,
          target: homecardTarget,
          onClick: handleClick,
          className: restProps.contentClassName
        })}
        {renderExtraContent(otherProps)}
      </>

      {hasDescription && (
        <div
          style={!isDescriptionVisible ? inlineStylesHidden : inlineStylesShown}
        >
          {renderDescription({
            ...otherProps,
            isBooked,
            description,
            link,
            linkTarget: homecardTarget,
            onCloseClick
          })}
        </div>
      )}
    </>
  );
};

ThemedHomecardWrapper.propTypes = {
  className: PropTypes.string,
  link: PropTypes.string.isRequired,
  newTab: PropTypes.bool,
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onTouchEnd: PropTypes.func,
  onTouchStart: PropTypes.func,
  renderContent: PropTypes.func,
  renderImage: PropTypes.func,
  selected: PropTypes.bool,
  theme: PropTypes.shape({
    wrapper: PropTypes.string,
    multilistingStack: PropTypes.string
  }),
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string
    })
  ),
  renderExtraContent: PropTypes.func,
  renderDescription: PropTypes.func,
  dataTest: propTypes.string,
  onMoreInfoClick: PropTypes.func,
  description: PropTypes.shape({}),
  isBooked: PropTypes.bool
};

export default memo(ThemedHomecardWrapper);
